<template>
  <li class="dropdown-item" :style="nodeMargin" :class="hasChildren? 'has-children' : 'no-children'">
    <div
      class="dropdown-inner-content"
      :class="{ open: showChildren }"
      @click="$event.stopPropagation()"
    >
      <img
        v-if="hasChildren"
        @click.stop="toggleChildren"
        src="@/assets/images/arrow_categories.svg"
        alt="arrow-icon"
        class="arrow-icon"
        :class="{ open: showChildren }"
      />
      <label class="checkbox-container">
        <input
          type="checkbox"
          name="values"
          :checked="
            mode === 'filter-bar'
              ? isClassificationSelected(
                  classificationKeyId,
                  classifications.id
                )
              : isChecked(classifications.id)
          "
          @change="
            mode === 'filter-bar'
              ? updateFilterSelection($event, classifications)
              : updateEntryClassifications(classifications, $event)
          "
        />
        <span
          class="checkmark"
          ref="rolesSelected"
        ></span>
      </label>
      <span class="text classification" @click.stop="toggleChildren">{{
        classifications.value
      }}</span>
    </div>
    <ul
      class="drop-down-sub-list"
      :class="hasChildren? 'has-children' : 'no-children'"
      v-if="hasChildren"
      v-show="showChildren"
    >
      <classifications-details
        class="menu-box"
        v-for="child in classifications.children"
        :key="child.id"
        :classifications="child"
        :classificationKey="classificationKey"
        :classificationKeyId="classificationKeyId"
        :collectionEntryClassifications="collectionEntryClassifications"
        :mode="mode"
        :spacing="spacing + 10"
        @updateEntryClassifications.stop="
          this.$emit('updateEntryClassifications', $event)
        "
        @updateClassifications="
          updateEntryClassifications(classifications, $event.event)
        "
        @updateFilter="updateFilterSelection($event, classifications)"
        @updateFilterSelection="this.$emit('updateFilterSelection', $event)"
      />
    </ul>
  </li>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { cloneDeep, isEqual } from 'lodash'

export default {
  name: 'classifications-details',
  props: {
    classifications: {
      type: Object,
      default: () => {}
    },
    collectionEntryClassifications: {
      type: Array,
      default: () => []
    },
    spacing: {
      type: Number,
      default: 0
    },
    classificationKey: {
      type: String,
      default: ''
    },
    classificationKeyId: {
      type: Number,
      default: null
    },
    mode: {
      type: String,
      default: null
    },
    borderSpacing: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      showChildren: false
    }
  },
  computed: {
    ...mapState({
      filtersObj: state => state.collections.filtersObj
    }),
    ...mapGetters(['isClassificationSelected']),
    nodeMargin () {
      return {
        'margin-inline-start': `${this.spacing}px`
      }
    },
    // Checks if a node(value classification) has children, if so the component recursion will continue and an arrow will be added next to the node.
    hasChildren () {
      const { children } = this.classifications
      return children && children.length > 0
    }
  },
  watch: {
    // For each update of selected classification the function isChecked is called in purpose to mark the right value in checkbox(true/false)
    collectionEntryClassifications () {
      for (const value of this.collectionEntryClassifications) {
        this.isChecked(value.id)
      }
    }
  },
  methods: {
    toggleChildren () {
      this.showChildren = !this.showChildren
    },
    // return whether for a given value if selected
    isChecked (id) {
      for (const classification of this.collectionEntryClassifications) {
        if (classification.id === id) {
          return true
        }
      }
      return false
    },
    /* update collectionEntryClassifications when a classification is added or remove. we store for each selected classification {id, key, value} and added it to collectionEntryClassifications. */
    updateEntryClassifications  (selectedClassification, event) {
      if (event.target) {
        // this stop was added to prevent closing of dropdown menu if checkbox is clicked
        event.stopPropagation()
      }
      /* In the first call to this function we are getting an event In the following readings we will get a Boolean value that signifies whether a classification has been added or removed */
      if (event === true || event.target?.checked) {
        const duplicate = this.isDuplicate(selectedClassification)
        // if true this classification should not be added again
        if (duplicate) {
          return
        }
        const obj = {
          id: selectedClassification.id,
          key: this.classificationKey,
          value: selectedClassification.value
        }
        this.collectionEntryClassifications.push(obj)
        // For a selected classification we must add all its descendants
        if (selectedClassification.children && selectedClassification.children.length && event.target) {
          this.addChildrenToSelectedClassifications(selectedClassification.children)
        }
        /* Because we want to add all the values that are at levels above the selected value we will emit to the same function with event = true.
        This is a recursive component and when performing an emit to the 'updateClassifications' function - the component listens for the calling and executes the updateEntryClassifications with the classification at the level above the selected classification */
        this.$emit('updateClassifications', { values: this.collectionEntryClassifications, event: true })
        this.showChildren = true
      } else {
        this.deleteClassification(selectedClassification)
      }
    },
    // Removes all descents of a removed classification. This is a recursive function
    deleteClassification (selectedClassification) {
      for (const [index, value] of this.collectionEntryClassifications.entries()) {
        // if selectedClassification was added to collectionEntryClassifications removed it
        if (value.id === selectedClassification.id) {
          this.collectionEntryClassifications.splice(index, 1)
          break
        }
      }
      /* If the selected classification has children they will be sent to the delete function and will also be removed from 'collectionEntryClassifications' */
      if (selectedClassification.children && selectedClassification.children.length) {
        for (const child of selectedClassification.children) {
          this.deleteClassification(child)
        }
      }
    },
    /* Checks that the selected classification does not exist in the collectionEntryClassifications. This situation occurs when 2 classifications of a certain level are selected and therefore the level above them should also be added */
    isDuplicate (selectedClassification) {
      for (const value of this.collectionEntryClassifications) {
        if (value.id === selectedClassification.id) {
          return true
        }
      }
      return false
    },
    // For a selected classification all its descendants will be added also. This is a recursive function
    addChildrenToSelectedClassifications (selectedClassification) {
      for (const value of selectedClassification) {
        const obj = {
          id: value.id,
          key: this.classificationKey,
          value: value.value
        }
        this.collectionEntryClassifications.push(obj)
        if (value.children) {
          this.addChildrenToSelectedClassifications(value.children)
        }
      }
    },
    async updateFilterSelection (event, value) {
      let isChecked = null
      // first click we add all children
      if (event.target) {
        isChecked = event.target.checked
        this.updateFilterSelectionChildren(value, isChecked)
      } else {
        isChecked = event
        this.addOrRemoveFilterToParams(value.id, isChecked)
        // add selected value without his children
        await this.$store.dispatch('addOrRemoveFilter', { filterKey: (this.classificationKeyId).toString(), filterValue: value.id, isChecked: isChecked })
      }
      // emitting only if adding classification
      if (isChecked) {
        this.$emit('updateFilter', isChecked)
      }
      this.showChildren = true
    },
    // add or remove valued from filter obj using 'addOrRemoveFilter' func. this is a recursive function
    async updateFilterSelectionChildren (value, isChecked) {
      this.addOrRemoveFilterToParams(value.id, isChecked)
      await this.$store.dispatch('addOrRemoveFilter', { filterKey: (this.classificationKeyId).toString(), filterValue: value.id, isChecked: isChecked })
      if (value.children) {
        for (const child of value.children) {
          await this.updateFilterSelectionChildren(child, isChecked)
        }
      }
    },
    // Adds or remove to URL the selected classifications ID
    addOrRemoveFilterToParams (valueId, isChecked) {
      if (!this.$route.query.filters) this.$route.query.filters = []
      const filterId = this.classificationKeyId + '-' + valueId
      const arrayOfFilters = this.$route.query.filters.length ? JSON.parse(this.$route.query.filters) : []
      const originalArrayOfFilters = cloneDeep(arrayOfFilters)
      const index = arrayOfFilters?.indexOf(filterId)
      const isFilterValueChecked = index > -1
      if (!isChecked && isFilterValueChecked) {
        arrayOfFilters.splice(index, 1)
      } else if (isChecked && !isFilterValueChecked) {
        arrayOfFilters.push(filterId)
      }
      if (!isEqual(arrayOfFilters, originalArrayOfFilters)) {
        const query = {
          searchText: this.$route.searchText
        }
        if (arrayOfFilters.length) {
          query.filters = JSON.stringify(arrayOfFilters)
        }
        this.$router.push({ query })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.text.classification{line-height: 1;}
.dropdown-inner-content .arrow-icon{
  transform: rotate(0deg); margin-inline-end: 4px;
  &.open{
    transform: rotate(90deg);
  }
  .ltr &{margin-inline-start: 1px;}
  .rtl &{margin-inline-start: 5px;}
}
</style>
